import { sqLicenseManagementStore, sqScalingModelStore, sqToolModelStore } from '@/core/core.stores';
import { flux } from '@/core/flux.module';
import { Feature } from '@/licenseManagement/licenseManagement.store';
import { isLockingAndVersioningEnabled } from '@/services/systemConfiguration.utilities';
import { fetchAllModelVersionMetadata, fetchModelItems } from '@/tools/models/model.api';
import { MODEL_TYPES } from '@/tools/models/model.types';
import { mergeVersionMetadata } from '@/tools/models/model.utilities';
import type { ModelOutputV1 } from '@/sdk/model/ModelOutputV1';
import type { ModelInput, ModelPayload } from '@/tools/models/model.store';
import type { ModelType } from '@/tools/models/model.types';

export function resetModel(modelType: ModelType) {
  flux.dispatch(`${modelType}_MODEL_RESET_MODEL`);
}

export function setModel(modelType: ModelType, model: ModelPayload) {
  flux.dispatch(`${modelType}_MODEL_SET_MODEL`, { model });
}

export function setFeatureToEditId(modelType: ModelType, featureId: string | undefined) {
  flux.dispatch(`${modelType}_MODEL_SET_FEATURE_TO_EDIT_ID`, { featureToEditId: featureId });
}

export const getModelInputFeatures = (modelType: ModelType): ModelInput[] => {
  if (modelType === MODEL_TYPES.TOOL) {
    return sqToolModelStore.inputFeatures;
  }
  if (modelType === MODEL_TYPES.SCALING) {
    return sqScalingModelStore.inputFeatures;
  }
  return [];
};

export function setInstanceToEditId(modelType: ModelType, instanceToEditId: string | undefined) {
  flux.dispatch(`${modelType}_MODEL_SET_INSTANCE_TO_EDIT_ID`, { instanceToEditId });
}

export function setModelOutput(modelType: ModelType, modelOutput: ModelOutputV1) {
  flux.dispatch(`${modelType}_MODEL_SET_MODEL_OUTPUT`, { modelOutput });
}

export function setModelVersionState(
  modelType: ModelType,
  versionState: { isLocked: boolean; isDraft: boolean; sourceVersionNumber?: number },
) {
  flux.dispatch(`${modelType}_MODEL_SET_VERSION_STATE`, versionState);
}

/**
 * Fetches model items for the given workbooks and enriches them with
 * version/draft metadata when the Locking feature is enabled.
 */
export async function fetchEnrichedModelItems(workbookIds: string[]) {
  const models = await fetchModelItems(workbookIds);
  const lockingEnabled = isLockingAndVersioningEnabled() && sqLicenseManagementStore.hasValidFeature(Feature.Locking);
  if (!lockingEnabled || models.length === 0) {
    return models;
  }

  try {
    const metadataMap = await fetchAllModelVersionMetadata(models.map((m) => m.id));
    return mergeVersionMetadata(models, metadataMap);
  } catch {
    return models;
  }
}
